import { gql } from '@apollo/client';

export const UPDATE_BARN_MUTATION = gql`
  mutation UpdateBarn(
    $barn_id: uuid!
    $barn_external_id: String
    $barn_name: String
    $barn_address: String
    $barn_settings: [barn_setting_insert_input!]!
    $upsert_bins: [bin_insert_input!]!
    $delete_bins: [bin_insert_input!]!
    $upsert_feed_lines: [feed_line_insert_input!]!
    $delete_feed_lines: [feed_line_insert_input!]!
    $upsert_bin_feed_lines: [bin_feed_line_insert_input!]!
    $delete_bin_feed_lines: [bin_feed_line_insert_input!]!
    $upsert_bin_sets: [bin_set_insert_input!]!
    $delete_bin_sets: [bin_set_insert_input!]!
    $upsert_feeders: [feeder_insert_input!]!
    $delete_feeders: [feeder_insert_input!]!
    $upsert_rooms: [room_insert_input!]!
    $delete_rooms: [room_insert_input!]!
    $upsert_room_feed_lines: [room_feed_line_insert_input!]!
    $delete_room_feed_lines: [room_feed_line_insert_input!]!
    $upsert_user_barns: [user_barn_insert_input!]!
    $delete_user_barns: [user_barn_insert_input!]!
    $canManageUsers: Boolean!
    $insert_barn_algorithm_override: [barn_algorithm_override_insert_input!]!
    $barn_id_for_override_delete: uuid!
    $deleted_at_timestamp: bigint!
    $should_execute_delete_override: Boolean!
    $hasStaffAccess: Boolean!
    $remap_bin_controllers: [bin_controller_updates!]!
    $remap_bin_controller_bins: [bin_controller_bin_insert_input!]!
    $swap_bin_controller_bins_phase1: [bin_controller_bin_insert_input!]!
    $swap_bin_controller_bins_phase2: [bin_controller_bin_insert_input!]!
    $upsert_device_configurations: [device_configuration_insert_input!]!
    $standalone_insert_bin_controllers: [bin_controller_insert_input!]!
    $standalone_upsert_bin_controller_bins: [bin_controller_bin_insert_input!]!
    $clear_device_configuration_updates: [device_configuration_updates!]!
    $soft_delete_bin_controller_updates: [bin_controller_updates!]!
    $soft_delete_bin_controller_bin_updates: [bin_controller_bin_updates!]!
    $insert_device_commands: [device_command_insert_input!]!
  ) {
    update_farm_by_pk(
      pk_columns: { id: $barn_id }
      _set: { name: $barn_name, external_id: $barn_external_id, address: $barn_address }
    ) {
      id
    }

    # User management operations - only included when canManageUsers is true
    upsert_user_barn: insert_user_barn(
      objects: $upsert_user_barns
      on_conflict: { constraint: user_barn_pkey, update_columns: [deleted_at, role_id] }
    ) @include(if: $canManageUsers) {
      affected_rows
    }

    delete_user_barn: insert_user_barn(
      objects: $delete_user_barns
      on_conflict: { constraint: user_barn_pkey, update_columns: [deleted_at] }
    ) @include(if: $canManageUsers) {
      affected_rows
    }

    upsert_bin_set: insert_bin_set(objects: $upsert_bin_sets) {
      affected_rows
    }
    # Remap bin_controller.bin_set_id for both auto-remapped and user-reassigned stale controllers.
    # Runs after upsert_bin_set so new bin sets exist before the FK is updated.
    remap_bin_controllers: update_bin_controller_many(updates: $remap_bin_controllers) {
      affected_rows
    }
    # Update bin_controller_bin rows to point to the new bins for user-selected remaps.
    # Uses INSERT ... ON CONFLICT so all bin_id swaps happen in a single statement — PostgreSQL
    # defers unique constraint checks until the end, avoiding transient duplicate violations.
    remap_bin_controller_bins: insert_bin_controller_bin(
      objects: $remap_bin_controller_bins
      on_conflict: { constraint: bin_controller_bin_controller_index_unique, update_columns: [bin_id, deleted_at] }
    ) {
      affected_rows
    }
    delete_bin_set: insert_bin_set(
      objects: $delete_bin_sets
      on_conflict: { constraint: bin_set_pkey, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    upsert_bin: insert_bin(
      objects: $upsert_bins
      on_conflict: { constraint: bin_pkey, update_columns: [name, external_id, capacity_in_grams, bin_set_id] }
    ) {
      affected_rows
    }
    delete_bin: insert_bin(objects: $delete_bins, on_conflict: { constraint: bin_pkey, update_columns: [deleted_at] }) {
      affected_rows
    }
    upsert_feed_line: insert_feed_line(
      objects: $upsert_feed_lines
      on_conflict: { constraint: feed_line_pkey, update_columns: [name, reference_id, bin_set_id] }
    ) {
      affected_rows
    }
    delete_feed_line: insert_feed_line(
      objects: $delete_feed_lines
      on_conflict: { constraint: feed_line_pkey, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    upsert_bin_feed_line: insert_bin_feed_line(
      objects: $upsert_bin_feed_lines
      on_conflict: { constraint: bin_feed_line_pkey, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    delete_bin_feed_line: insert_bin_feed_line(
      objects: $delete_bin_feed_lines
      on_conflict: { constraint: bin_feed_line_pkey, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    upsert_feeder: insert_feeder(
      objects: $upsert_feeders
      on_conflict: { constraint: feeder_pkey, update_columns: [capacity_in_grams, quantity] }
    ) {
      affected_rows
    }
    delete_feeder: insert_feeder(
      objects: $delete_feeders
      on_conflict: { constraint: feeder_pkey, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    upsert_room: insert_room(
      objects: $upsert_rooms
      on_conflict: { constraint: room_pkey, update_columns: [name, reference_id] }
    ) {
      affected_rows
    }
    delete_room: insert_room(
      objects: $delete_rooms
      on_conflict: { constraint: room_pkey, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    upsert_room_feed_line: insert_room_feed_line(
      objects: $upsert_room_feed_lines
      on_conflict: { constraint: room_feed_line_room_id_feed_line_id_unique, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    delete_room_feed_line: insert_room_feed_line(
      objects: $delete_room_feed_lines
      on_conflict: { constraint: room_feed_line_room_id_feed_line_id_unique, update_columns: [deleted_at] }
    ) {
      affected_rows
    }
    upsert_barn_setting: insert_barn_setting(
      objects: $barn_settings
      on_conflict: { constraint: barn_setting_type_version_farm_id_unique, update_columns: [settings] }
    ) {
      affected_rows
    }
    delete_barn_algorithm_override: update_barn_algorithm_override(
      where: { barn_id: { _eq: $barn_id_for_override_delete }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deleted_at_timestamp }
    ) @include(if: $should_execute_delete_override) {
      affected_rows
    }
    insert_barn_algorithm_override: insert_barn_algorithm_override(
      objects: $insert_barn_algorithm_override
    ) @include(if: $hasStaffAccess) {
      affected_rows
    }
    # Swap bin order: ON CONFLICT (pkey) updates one row at a time, so swapping index 0↔1 in one
    # INSERT hits UNIQUE(bin_controller_id, index) mid-flight. Phase 1 moves both rows to temp
    # indices 2 and 3; phase 2 assigns final 0/1 (bin that was at 1 first, then bin that was at 0)
    # so UNIQUE(bin_controller_id, bin_id) never sees a duplicate bin_id on the controller.
    swap_bin_controller_bins_phase1: insert_bin_controller_bin(
      objects: $swap_bin_controller_bins_phase1
      on_conflict: { constraint: bin_controller_bin_pkey, update_columns: [index] }
    ) {
      affected_rows
    }
    swap_bin_controller_bins_phase2: insert_bin_controller_bin(
      objects: $swap_bin_controller_bins_phase2
      on_conflict: { constraint: bin_controller_bin_pkey, update_columns: [index] }
    ) {
      affected_rows
    }
    # Insert new device_configuration rows for controllers whose config blob changed.
    upsert_device_configurations: insert_device_configuration(
      objects: $upsert_device_configurations
    ) {
      affected_rows
    }
    # Add bin controller + bins outside full barn save (same on_conflict as former InsertBinControllerWithBins).
    standalone_insert_bin_controllers: insert_bin_controller(
      objects: $standalone_insert_bin_controllers
      on_conflict: { constraint: bin_controller_device_id_unique, update_columns: [bin_set_id, deleted_at] }
    ) {
      affected_rows
    }
    # Reactivating a controller reuses the same bin_controller row; junction rows may still exist
    # soft-deleted. Nested insert hits UNIQUE(bin_controller_id, index) on those rows — use root
    # upsert (same as remap_bin_controller_bins) after the parent upsert.
    standalone_upsert_bin_controller_bins: insert_bin_controller_bin(
      objects: $standalone_upsert_bin_controller_bins
      on_conflict: { constraint: bin_controller_bin_controller_index_unique, update_columns: [bin_id, deleted_at] }
    ) {
      affected_rows
    }
    # Remove flow: soft-delete controller then junction rows (matches former SoftDeleteBinController).
    soft_delete_bin_controllers_adhoc: update_bin_controller_many(updates: $soft_delete_bin_controller_updates) {
      affected_rows
    }
    soft_delete_bin_controller_bins_adhoc: update_bin_controller_bin_many(updates: $soft_delete_bin_controller_bin_updates) {
      affected_rows
    }
    # End active device_configuration rows (e.g. after removing a bin controller).
    clear_active_device_configurations: update_device_configuration_many(updates: $clear_device_configuration_updates) {
      affected_rows
    }
    # Send "Set Configuration" command to every controller that needs to re-read its config.
    # Runs last so the device is notified only after all data has been committed.
    insert_device_commands: insert_device_command(
      objects: $insert_device_commands
    ) {
      affected_rows
    }
  }
`;

export const LOOKUP_DEVICE_BY_SERIAL_QUERY = gql`
  query LookupBinControllerDeviceBySerial($serial: String!) {
    device(
      where: {
        serial: { _eq: $serial }
        deleted_at: { _is_null: true }
        _not: { bin_controller: { deleted_at: { _is_null: true } } }
      }
      limit: 1
    ) {
      id
      serial
      bin_controller {
        id
      }
    }
  }
`;

export const GET_BARN_SETTINGS_GQL = gql`
  query BarnSettingsQuery($barnId: uuid!, $type: String, $version: Int) {
    barn_setting(where: { farm_id: { _eq: $barnId }, type: { _eq: "calibration_settings" }, version: { _eq: 0 } }) {
      settings
    }
    auto_complete_delivery_setting: barn_setting(
      where: { farm_id: { _eq: $barnId }, type: { _eq: "delivery_autocompelete" }, version: { _eq: 1 } }
    ) {
      settings
    }
    notification_animalGroupSnooze_setting: barn_setting(
      where: { farm_id: { _eq: $barnId }, type: { _eq: "notification_animalGroupSnooze" }, version: { _eq: 1 } }
    ) {
      settings
    }
    bin_inventory_management_setting: barn_setting(
      where: {
        farm_id: { _eq: $barnId }
        type: { _eq: "bin_inventory_management" }
        version: { _eq: 0 }
        deleted_at: { _is_null: true }
      }
    ) {
      settings
    }
    features_setting: barn_setting(
      where: {
        farm_id: { _eq: $barnId }
        type: { _eq: "features" }
        version: { _eq: 0 }
        deleted_at: { _is_null: true }
      }
    ) {
      settings
    }
    bin_controller_network_setting: barn_setting(
      where: {
        farm_id: { _eq: $barnId }
        type: { _eq: "bin_controller_network" }
        version: { _eq: 0 }
        deleted_at: { _is_null: true }
      }
    ) {
      settings
    }
    barn: farm_by_pk(id: $barnId) {
      id
      organization_id
      external_id
      name
      address
      organization {
        id
        selected_notification_plan {
          plan_id
          plan {
            name
            tiers(order_by: { sort_order: asc }) {
              id
              name
              sort_order
            }
          }
        }
      }
      user_barns(where: { deleted_at: { _is_null: true } ,user: { deleted_at: { _is_null: true } } }) {
        user_id
        role_id
        user_barn_notification_tier_assignment {
          id
          tier_id
        }
        user {
          id
          user_organizations(
            where: {
              organization: { farms: { id: { _eq: $barnId } } }
              deleted_at: { _is_null: true }
            }
            limit: 1
          ) {
            role {
              id
              name
            }
            user_organization_notification_tier_assignment {
              tier_id
            }
          }
        }
      }
      user_notification_tier_assignments(where: { barn_id: { _eq: $barnId }, user: { deleted_at: { _is_null: true } } }) {
        id
        tier_id
        user_id
        user {
          id
          name
          email
          user_organizations(
            where: {
              organization: { farms: { id: { _eq: $barnId } } }
              deleted_at: { _is_null: true }
            }
            limit: 1
          ) {
            role {
              id
              name
            }
            user_organization_notification_tier_assignment {
              tier_id
            }
          }
        }
      }
      bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
        name
        bin_set_id
        external_id
        capacity_in_grams
      }
      feed_lines(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
        name
        reference_id
        bin_set_id
        bin_assignment(where: { deleted_at: { _is_null: true } }, order_by: { bin: { name: asc } }) {
          bin_id
        }
        feeders(where: { deleted_at: { _is_null: true } }) {
          id
          feed_line_id
          capacity_in_grams
          quantity
        }
        bin_feed_lines(where: { deleted_at: { _is_null: true } }) {
          bin_id
          feed_line_id
        }
      }
      bin_sets(where: { deleted_at: { _is_null: true } }) {
        id
        feed_lines {
          id
        }
        bins(where: { deleted_at: { _is_null: true } }) {
          id
          name
        }
        bin_controllers(where: { deleted_at: { _is_null: true } }, limit: 1) {
          id
          device_id
          bin_set_id
          bin_controller_bins(where: { deleted_at: { _is_null: true } }, order_by: { index: asc }) {
            id
            bin_id
            index
            bin {
              id
              name
            }
          }
          device {
            serial
            device_configurations(
              where: { ended_at: { _is_null: true }, deleted_at: { _is_null: true } }
              order_by: { started_at: desc }
              limit: 1
            ) {
              id
              data
              version
            }
          }
        }
      }
      rooms(where: { deleted_at: { _is_null: true } }) {
        id
        name
        reference_id
        room_feed_lines(where: { deleted_at: { _is_null: true } }) {
          feed_line_id
        }
      }
    }
    roles(
      where: { _or: [{ organization: { farms: { id: { _eq: $barnId } } } }, { organization_id: { _is_null: true } }] }
    ) {
      id
      name
      privileges
    }
  }
`;

export const GET_USER_LIST_GQL = gql`
  query UserListQuery {
    user(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
      id
      name
      email
    }
  }
`;

export const GET_ALGORITHM_VERSIONS_GQL = gql`
  query AlgorithmVersionsQuery {
    algorithm(where: { deleted_at: { _is_null: true } }, order_by: { created_at: desc }) {
      version
      description
      published_at
    }
  }
`;

export const GET_BARN_ALGORITHM_OVERRIDE_GQL = gql`
  query BarnAlgorithmOverrideQuery($barnId: uuid!) {
    barn_algorithm_override(where: { barn_id: { _eq: $barnId }, deleted_at: { _is_null: true } }) {
      id
      algorithm_version
      algorithm {
        version
        description
        published_at
      }
    }
  }
`;

export const UPSERT_USER_BARN_TIER_ASSIGNMENT_GQL = gql`
  mutation UpsertUserBarnTierAssignment($objects: [user_barn_notification_tier_assignment_insert_input!]!) {
    insert_user_barn_notification_tier_assignment(
      objects: $objects
      on_conflict: { constraint: user_barn_notification_tier_assignment_unique, update_columns: [tier_id] }
    ) {
      affected_rows
      returning {
        id
        tier_id
      }
    }
  }
`;

export const DELETE_USER_BARN_TIER_ASSIGNMENT_GQL = gql`
  mutation DeleteUserBarnTierAssignment($id: uuid!) {
    delete_user_barn_notification_tier_assignment(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;
